/**
 * PasskeyAuthFlow — renders the passkey login UI.
 *
 * Two patterns supported:
 *
 *   (a) Simple prop-driven (original):
 *       `onPasskeyLogin()` callback; app owns the WebAuthn flow entirely.
 *
 *   (b) Option-builder (for cross-domain / tenant flows with subdomain):
 *       `onAuthenticate({ subdomain })` callback called with the subdomain
 *       context, returning a Promise that resolves on auth success and rejects
 *       on error. If `onFallback` is provided, a "Use email & password" link
 *       is rendered so the user can switch auth methods.
 *
 * Props are all optional except the callback — use EITHER `onPasskeyLogin`
 * OR `onAuthenticate`. `onAuthenticate` takes precedence if both are present.
 *
 * @module @puffin-core/login-shell/components/PasskeyAuthFlow
 */

import { useCallback } from 'react';
import PropTypes from 'prop-types';
// The NARROW subpath, never the barrel: login-shell is router-free (see ContactManagerModal).
import { InlineFeedback } from '@puffin-core/page-shell/inline-feedback';
import { useLoginTranslate } from '../hooks/useLoginTranslate.js';

export function PasskeyAuthFlow({
  onPasskeyLogin,
  onAuthenticate,
  onFallback,
  subdomain,
  passkeySupported = true,
  platformAuthAvailable = true,
  loading = false,
  error = null,
  clearError,
  unlockMode = false,
  t,
}) {
  const handleClick = useCallback(() => {
    if (onAuthenticate) {
      return onAuthenticate({ subdomain });
    }
    if (onPasskeyLogin) {
      return onPasskeyLogin();
    }
    return undefined;
  }, [onAuthenticate, onPasskeyLogin, subdomain]);

  // ⚠️ RECIPE §3: `useLoginTranslate` is a CUSTOM hook and must follow the
  // computed ones (`web/component-state-order`). `handleClick` does not read
  // `translate` — only the JSX below does — so binding it here satisfies the
  // rule with no TDZ risk.
  const translate = useLoginTranslate(t);

  // Resolved into a named slot above the returns: it is the only branch carrying a
  // NESTED conditional (the retry action), so lifting it takes two conditionals out
  // of the final return in one move.
  // SAVE-FEEDBACK-001 (SAVE-FEEDBACK-CANON-001 P3): the passkey attempt's failure is the ONE feedback
  // component — `role="alert"` is its, and "Try again" is its action row — never hand-written `.alert`
  // markup. `.passkey-auth-flow` spaces its children with a gap, so the old margin is not needed.
  const errorAlert = error ? (
    <InlineFeedback
      tone="error"
      message={error}
      testId="passkey-error"
      actions={
        clearError
          ? [
              {
                key: 'retry',
                label: translate('login.passkey.tryAgain'),
                onClick: clearError,
                testId: 'passkey-retry-btn',
              },
            ]
          : undefined
      }
    />
  ) : null;

  if (!passkeySupported) {
    return (
      <div className="passkey-auth-flow" data-testid="passkey-auth-flow">
        <div className="passkey-unsupported" role="status" data-testid="passkey-unsupported">
          <p className="text-muted text-center">{translate('passkey.unsupported')}</p>
        </div>
        {onFallback && (
          <div className="flex justify-center mt-md">
            <button
              type="button"
              className="btn btn-link text-sm"
              onClick={onFallback}
              data-testid="passkey-fallback-btn"
            >
              {translate('login.passkey.fallback')}
            </button>
          </div>
        )}
      </div>
    );
  }

  if (!platformAuthAvailable && !loading && !error) {
    return (
      <div className="passkey-auth-flow" data-testid="passkey-auth-flow">
        <div className="passkey-unsupported" role="status" data-testid="passkey-no-platform">
          <p className="text-muted text-center">{translate('passkey.noPlatform')}</p>
        </div>
        <div className="flex justify-center mt-md">
          <button
            type="button"
            className="passkey-button btn btn-primary btn-lg"
            onClick={handleClick}
            disabled={loading}
            data-testid="passkey-login-btn"
          >
            {translate(
              unlockMode ? 'login.passkey.unlockSecurityKey' : 'login.passkey.submitSecurityKey',
            )}
          </button>
        </div>
        {onFallback && (
          <div className="flex justify-center mt-md">
            <button
              type="button"
              className="btn btn-link text-sm"
              onClick={onFallback}
              data-testid="passkey-fallback-btn"
            >
              {translate('login.passkey.fallback')}
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="passkey-auth-flow" data-testid="passkey-auth-flow">
      {errorAlert}

      {loading && (
        <div
          className="flex-col items-center gap-md p-lg"
          role="status"
          aria-live="polite"
          data-testid="passkey-loading"
        >
          <p className="text-muted text-center">{translate('passkey.loading')}</p>
        </div>
      )}

      <div className="flex justify-center mt-md">
        <button
          type="button"
          className="passkey-button btn btn-primary btn-lg"
          onClick={handleClick}
          disabled={loading}
          aria-busy={loading ? 'true' : 'false'}
          data-testid="passkey-login-btn"
        >
          {translate(unlockMode ? 'login.passkey.unlock' : 'login.passkey.submit')}
        </button>
      </div>

      {onFallback && (
        <div className="flex justify-center mt-md">
          <button
            type="button"
            className="btn btn-link text-sm"
            onClick={onFallback}
            data-testid="passkey-fallback-btn"
          >
            {translate('login.passkey.fallback')}
          </button>
        </div>
      )}
    </div>
  );
}

PasskeyAuthFlow.propTypes = {
  onPasskeyLogin: PropTypes.func,
  onAuthenticate: PropTypes.func,
  onFallback: PropTypes.func,
  subdomain: PropTypes.string,
  passkeySupported: PropTypes.bool,
  platformAuthAvailable: PropTypes.bool,
  loading: PropTypes.bool,
  error: PropTypes.string,
  clearError: PropTypes.func,
  /** A locked register's unlock (POS-OFFLINE-STATION-LOCK-001 U14'd-2): the button says "Unlock", never "Sign in". */
  unlockMode: PropTypes.bool,
  t: PropTypes.func,
};

export default PasskeyAuthFlow;
